<template>
	<div class="subscription-container">
		<!-- Loading State -->
		<section v-if="loading" class="loading-section">
			<div class="spinner"></div>
			<p>Loading subscription information...</p>
		</section>

		<!-- Current Subscription Info (for Plus subscribers) -->
		<section v-else-if="subscriptionType === 'plus'" class="subscription-info">
			<h2 class="subscription-title">Your Subscription</h2>

			<div class="current-plan">
				<div class="plan-badge plus">PLUS</div>

				<div class="plan-details">
					<h3>GrubVision Plus</h3>
					<p>You are currently subscribed to our plus plan.</p>
					<p v-if="endDate">
						Subscription ends: {{ new Date(endDate).toLocaleDateString() }}
					</p>
				</div>
			</div>

			<!-- Management Link -->
			<div class="action-buttons">
				<a
					v-if="manageUrl && platformActive === 'web'"
					:href="manageUrl"
					class="manage-button">
					Manage Subscription
				</a>
				<p v-else-if="platformActive === 'trial'">
					Enjoy the trial period and explore the features.
				</p>
				<p v-else-if="platformActive === 'gifted_feedback'">
					Enjoy the subscription that was gifted to you for being a tester.
				</p>
				<p v-else-if="platformActive === 'gifted_forever'">
					You have a lifelong subscription on the house!
				</p>
				<p
					v-else-if="
						platformActive !== 'web' &&
						platformActive !== null
					"
					class="subscription-message">
					Your subscription is managed through
					{{ getPlatformName(platformActive) }}
				</p>
				<p v-else class="subscription-message">
					Your subscription information cannot be managed at this time
				</p>
			</div>
		</section>

		<!-- Free Plan Info -->
		<section
			v-else-if="subscriptionType === 'basic' && !success && message === ''"
			class="subscription-info">
			<h2 class="subscription-title">Your Subscription</h2>

			<div class="current-plan">
				<div class="plan-badge basic">FREE</div>

				<div class="plan-details">
					<h3>GrubVision Free Plan</h3>
					<p>
						You are currently using the free plan. Upgrade to Plus for more
						features!
					</p>
				</div>
			</div>

			<h3 class="upgrade-title">Upgrade to plus</h3>

			<!-- Plus Yearly Plan -->
			<div class="product-card">
				<div class="description">
					<h3>Plus Yearly</h3>
					<h5>$49.99 / year</h5>
					<p class="product-description">
						Best value plan with all plus features
					</p>
				</div>
				<button
					class="checkout-button"
					@click="createCheckoutSession('yearly')">
					Subscribe Yearly
				</button>
			</div>

			<!-- Plus Monthly Plan -->
			<div class="product-card">
				<div class="description">
					<h3>Plus Monthly</h3>
					<h5>$4.99 / month</h5>
					<p class="product-description">Flexible monthly subscription</p>
				</div>
				<button
					class="checkout-button"
					@click="createCheckoutSession('monthly')">
					Subscribe Monthly
				</button>
			</div>
		</section>

		<!-- Success Display (after checkout) -->
		<section v-else-if="success && sessionId !== ''">
			<div class="product">
				<div class="description">
					<h3>Subscription successful!</h3>
					<p>Thank you for subscribing to GrubVision Plus!</p>
				</div>
			</div>
			<form @submit.prevent="createPortalSession">
				<input
					type="hidden"
					id="session-id"
					name="session_id"
					:value="sessionId" />
				<button id="portal-button" type="submit">
					Manage your billing information
				</button>
			</form>
		</section>

		<!-- Message Display (for errors) -->
		<section v-else>
			<p>{{ message }}</p>
		</section>
	</div>
</template>

<script setup>
	import { ref, onMounted, onUnmounted } from "vue"
	import { fetchWithAuth } from "@/utils/api-client"
	import { useUserStore } from "@/stores/user"
	import { Capacitor } from "@capacitor/core"
	import Swal from "sweetalert2"
	import { StripeCheckout } from "@/plugins/StripeCheckout"

	const userStore = useUserStore()

	// State variables
	const message = ref("")
	const success = ref(false)
	const sessionId = ref("")
	const loading = ref(true)
	const subscriptionType = ref("basic")
	const manageUrl = ref(null)
	const platformActive = ref(null)
	const endDate = ref(null)
	const stripeListenerHandles = []

	// Price lookup keys for the different plans

	// stripe sandbox product keys
	// const priceKeys = {
	// 	yearly: "prod_S1WYNwLJnIQOcw",
	// 	monthly: "prod_S1WXThE9sXnCf2",
	// }

	//stripe live product keys
	const priceKeys = {
		yearly: "prod_S2yONLNNQuv0FZ",
		monthly: "prod_S2yMiuM53CPGsM",
	}

	// Get platform display name
	const getPlatformName = (platform) => {
		switch (platform) {
			case "android":
				return "Google Play Store"
			case "ios":
				return "Apple App Store"
			default:
				return platform
		}
	}

	const isIOSNative = () =>
		Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios"

	// Fetch subscription info
	const fetchSubscriptionInfo = async () => {
		try {
			loading.value = true
			const response = await fetchWithAuth("/api/get-subscription")

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`)
			}

			const data = await response.json()
			subscriptionType.value = data.subscriptionType
			manageUrl.value = data.manageUrl
			platformActive.value = data.platformActive
			endDate.value = data.endDate

			if (userStore.user) {
				userStore.user.subscription_type = data.subscriptionType
				userStore.user.platform_active = data.platformActive
			}
		} catch (error) {
			console.error("Error fetching subscription info:", error)
			message.value = "Failed to load subscription information."
		} finally {
			loading.value = false
		}
	}

	const waitForSubscriptionUpdate = async () => {
		loading.value = true
		const maxRetries = 10
		const delay = 1000 // 1 second

		for (let i = 0; i < maxRetries; i++) {
			console.log(`Polling for subscription update... Attempt ${i + 1}/${maxRetries}`)
			await fetchSubscriptionInfo()

			if (subscriptionType.value === "plus") {
				console.log("Subscription updated to plus!")
				loading.value = false
				return
			}

			if (i < maxRetries - 1) {
				await new Promise((resolve) => setTimeout(resolve, delay))
			}
		}

		loading.value = false
		// If we reach here, it means we timed out without seeing the upgrade
		console.log("Polling timed out. Subscription still basic.")
		Swal.fire({
			icon: "info",
			title: "Processing Subscription",
			text: "We received your payment, but the subscription update is taking a bit longer than usual. You may need to re-login to see the changes.",
			confirmButtonText: "OK",
            confirmButtonColor: "#5469d4",
		})
	}

	const applyCheckoutRedirect = async (event = {}) => {
		console.log("applyCheckoutRedirect called with:", event)
		const query = event.query || {}
		const status = event.status || ""

		const isSuccess = status === "success" || query.success === "true"
		const isCanceled = status === "canceled" || query.canceled === "true"

		if (isSuccess) {
			success.value = true
			sessionId.value = event.sessionId || query.session_id || ""
			message.value = ""
			await waitForSubscriptionUpdate()
		} else if (isCanceled) {
			success.value = false
			//message.value =
			//	"Order canceled -- continue to shop around and checkout when you're ready."
			await fetchSubscriptionInfo()
		} else {
			await fetchSubscriptionInfo()
		}
	}

	const setupStripeListeners = async () => {
		if (!isIOSNative()) return

		const redirectHandle = await StripeCheckout.addListener(
			"checkoutRedirect",
			async (event) => {
				await applyCheckoutRedirect(event)
			},
		)

		const closeHandle = await StripeCheckout.addListener(
			"checkoutClosed",
			(event) => {
				if (event.reason === "user_closed") {
					// message.value = "Checkout closed."
				}
			},
		)

		const errorHandle = await StripeCheckout.addListener(
			"checkoutError",
			(event) => {
				console.error("Stripe checkout error:", event)
				message.value =
					event.message || "Checkout failed to load. Please try again."
			},
		)

		stripeListenerHandles.push(
			redirectHandle,
			closeHandle,
			errorHandle,
		)
	}

	const teardownStripeListeners = async () => {
		for (const handle of stripeListenerHandles.splice(0)) {
			try {
				await handle.remove()
			} catch (error) {
				console.error("Failed to remove Stripe checkout listener:", error)
			}
		}

		try {
			await StripeCheckout.removeAllListeners()
		} catch (error) {
			console.error("Failed to clear Stripe checkout listeners:", error)
		}
	}

	// Check if redirected from Checkout and fetch subscription info
	onMounted(async () => {
		const query = new URLSearchParams(window.location.search)

		if (query.get("success")) {
			success.value = true
			sessionId.value = query.get("session_id")
		}

		if (query.get("canceled")) {
			success.value = false
			message.value =
				"Order canceled -- continue to shop around and checkout when you're ready."
		}

		await setupStripeListeners()
		await fetchSubscriptionInfo()
	})

	// Create Stripe checkout session
	const createCheckoutSession = async (planType) => {
		try {

			const nativeIOS = isIOSNative()
			

			const response = await fetchWithAuth("/api/create-checkout-session", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					lookup_key: priceKeys[planType],
					product_id:
						planType === "yearly"
							? "prod_S1WYNwLJnIQOcw"
							: "prod_S1WXThE9sXnCf2",
					is_ios: nativeIOS
				}),
			})

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`)
			}

			const data = await response.json()

			if (nativeIOS) {
				await StripeCheckout.open({ url: data.url })
			} else {
				window.location.href = data.url
			}
		} catch (error) {
			console.error("Error creating checkout session:", error)
			message.value = "An error occurred while processing your request."
		}
	}

	// Create Stripe customer portal session
	const createPortalSession = async () => {
		try {
			const nativeIOS = isIOSNative()


			const response = await fetchWithAuth("/api/create-portal-session", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					session_id: sessionId.value,
					is_ios: nativeIOS
				}),
			})

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`)
			}

			const data = await response.json()
			if (nativeIOS) {
				await StripeCheckout.open({ url: data.url })
			} else {
				window.location.href = data.url
			}
		} catch (error) {
			console.error("Error creating portal session:", error)
			message.value = "An error occurred while processing your request."
		}
	}

	onUnmounted(() => {
		void teardownStripeListeners()
	})
</script>

<style lang="less" scoped>
	.subscription-container {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 2rem;
		font-family: -apple-system, BlinkMacSystemFont, sans-serif;
		max-width: 650px;
		margin: 0 auto;
	}

	.subscription-title {
		text-align: center;
		margin-bottom: 2rem;
		color: #32325d;
	}

	.upgrade-title {
		text-align: center;
		margin: 2rem 0 1.5rem;
		color: #32325d;
		font-size: 1.2rem;
	}

	section {
		background: #ffffff;
		display: flex;
		flex-direction: column;
		width: 100%;
		border-radius: 6px;
		padding: 2rem;
		box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.1);
	}

	.loading-section {
		align-items: center;
		justify-content: center;
		text-align: center;
		min-height: 200px;
	}

	.spinner {
		border: 4px solid rgba(0, 0, 0, 0.1);
		width: 36px;
		height: 36px;
		border-radius: 50%;
		border-left-color: #5469d4;
		animation: spin 1s linear infinite;
		margin-bottom: 1rem;
	}

	@keyframes spin {
		0% {
			transform: rotate(0deg);
		}
		100% {
			transform: rotate(360deg);
		}
	}

	.current-plan {
		display: flex;
		align-items: flex-start;
		padding: 1.5rem;
		border: 1px solid #e6e6e6;
		border-radius: 8px;
		margin-bottom: 2rem;
	}

	.plan-badge {
		padding: 0.5rem 1rem;
		border-radius: 4px;
		font-weight: bold;
		font-size: 0.875rem;
		margin-right: 1.5rem;
		min-width: 80px;
		text-align: center;

		&.plus {
			background-color: #6772e5;
			color: white;
		}

		&.basic {
			background-color: #b7b7b7;
			color: white;
		}
	}

	.plan-details {
		flex-grow: 1;
	}

	.action-buttons {
		margin-top: 1rem;
		display: flex;
		justify-content: center;
	}

	.manage-button {
		background: #5469d4;
		color: #ffffff;
		border: 0;
		border-radius: 4px;
		padding: 12px 24px;
		font-size: 16px;
		font-weight: 600;
		cursor: pointer;
		transition: background 0.2s ease;
		text-decoration: none;
		display: inline-block;

		&:hover {
			background: #4559c9;
		}
	}

	.subscription-message {
		color: #525f7f;
		font-style: italic;
		text-align: center;
	}

	.product-card {
		display: flex;
		flex-direction: column;
		padding: 1.5rem;
		margin-bottom: 1.5rem;
		border: 1px solid #e6e6e6;
		border-radius: 8px;
		transition: all 0.3s ease;
		position: relative;
	}

	.product-card:hover {
		box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1);
		transform: translateY(-2px);
	}

	.product {
		display: flex;
		margin-bottom: 2rem;
	}

	.description {
		display: flex;
		flex-direction: column;
		flex-grow: 1;
	}

	.product-description {
		margin: 0.5rem 0;
		color: #525f7f;
	}

	h3 {
		margin: 0 0 0.5rem 0;
		font-size: 1.25rem;
		color: #32325d;
	}

	h5 {
		margin: 0;
		font-size: 1rem;
		font-weight: 700;
		color: #6772e5;
	}

	button {
		background: #5469d4;
		color: #ffffff;
		border: 0;
		border-radius: 4px;
		padding: 12px 16px;
		font-size: 16px;
		font-weight: 600;
		cursor: pointer;
		transition: background 0.2s ease;
		display: block;
		width: 100%;
		margin-top: 1rem;
	}

	button:hover {
		background: #4559c9;
	}

	.checkout-button {
		margin-top: 1.5rem;
		align-self: flex-end;
	}
</style>
